import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { ChangeProgramModalProps } from 'interfaces/patients/patientInterfaces';
import { endOfDay, format, isValid, parse, startOfDay } from 'date-fns';
import {
  modalFormInputStyle,
  modalFormStyle,
  modalFormWrapInputsStyle,
  modalFormWrapStyle,
  modalHeaderStyle,
  modalSubtitleStyle,
  modalTitleStyle,
} from 'ui/modals/modalGlobal';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Combobox } from '@bestdoctor/spectrum-uikit/Combobox';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';
import { DatePicker } from '@bestdoctor/spectrum-uikit/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button as SpectrumButton } from '@bestdoctor/spectrum-uikit/Button';
import { dialogStyleAddModal } from 'ui/modals/modalAdd/modalAdd';
import { useSnackbar } from 'notistack';
import { useEditProgram } from 'hooks/programs/useEditProgram';

import { disabledDates } from 'utils/dateHelpers';
import { TooltipActionsInfo } from 'ui/tooltips/tooltipAction';
import { swapElements } from 'utils/swapElements';
import { analyticsTracker } from 'utils/analytics';
import { validationProgram, ProgramParams } from './validationChangeProgram';

export const ChangeProgramModal: React.FC<ChangeProgramModalProps> = ({
  open,
  parentClose,
  handleClose,
  periodByAttachOrDetach,
  patientData,
  programs,
  refetchPatient,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    setValue,
    reset,
    formState: { errors, isDirty },
    setError,
    handleSubmit,
    watch,
    control,
  } = useForm<ProgramParams>({
    mode: 'onTouched',
    resolver: zodResolver(validationProgram),
  });

  const getDisplayDate = (value?: string): string | undefined => {
    if (!value || value === '') return undefined;
    try {
      const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        return format(parsedDate, 'dd.MM.yyyy');
      }
    } catch {
      // Если не удалось распарсить, возвращаем как есть
    }
    return value;
  };

  // Для Spectrum DatePicker нужен отдельный input-state, иначе ручной ввод будет откатываться
  const changeAtWatched = watch('change_at');
  const changeAtDisplay = useMemo(() => getDisplayDate(changeAtWatched), [changeAtWatched]);
  const [changeAtInput, setChangeAtInput] = useState<string | undefined>(changeAtDisplay);

  useEffect(() => {
    setChangeAtInput(changeAtDisplay);
  }, [changeAtDisplay]);

  const disabledDays = (date: Date): boolean => {
    const actualDayTimeStamp = startOfDay(date).getTime();

    const canDetachSinceTimestamp = periodByAttachOrDetach
      ? startOfDay(parse(periodByAttachOrDetach.canDetachSince, 'yyyy-MM-dd', new Date())).getTime()
      : null;
    const canDetachSinceTimestampMax = periodByAttachOrDetach
      ? endOfDay(parse(periodByAttachOrDetach.canAttachOrDetachMaxDate, 'yyyy-MM-dd', new Date())).getTime()
      : null;
    if (disabledDates.some((item) => item === date.getTime())) {
      return disabledDates.some((item) => item === date.getTime());
    }

    if (canDetachSinceTimestamp && canDetachSinceTimestampMax) {
      return actualDayTimeStamp < canDetachSinceTimestamp || actualDayTimeStamp > canDetachSinceTimestampMax;
    }
    if (canDetachSinceTimestamp) {
      return actualDayTimeStamp < canDetachSinceTimestamp;
    }
    if (canDetachSinceTimestampMax) {
      return actualDayTimeStamp > canDetachSinceTimestampMax;
    }
    return false;
  };

  const modalClose: () => void = () => {
    setValue('change_at', '');
    handleClose();
  };

  const defaultProgram = useMemo(
    () =>
      patientData?.program_uuid
        ? programs[programs.map((program) => program.uuid).indexOf(patientData?.program_uuid)]
        : null,
    [patientData, programs]
  );

  useEffect(() => {
    if (patientData?.program_uuid) {
      setValue('new_program_uuid', patientData?.program_uuid);
    }
  }, [open, patientData, setValue]);

  const { isLoadingEditProgram, handleEditProgram } = useEditProgram({
    patientUuid: patientData ? patientData.uuid : null,
    onSuccess: (res) => {
      if (res.ok) {
        enqueueSnackbar('Программа успешно изменена!', { variant: 'success' });
        analyticsTracker.trackPatientCardChangeProgram();
        modalClose();
        refetchPatient();
        parentClose();
      } else {
        res.json().then(
          (data: {
            message: string;
            errors: {
              message: string;
              code: string;
              field: 'new_program_uuid' | 'change_at';
            }[];
          }) => {
            data.errors.forEach((item) => {
              const { field } = item;
              enqueueSnackbar(item.message, { variant: 'error' });
              setError(field, {
                type: 'custom',
                message: item.message,
              });
            });
          }
        );
      }
    },
    onError: (res) => {
      res.json().then(
        (data: {
          message: string;
          errors: {
            message: string;
            code: string;
            field: 'new_program_uuid' | 'change_at';
          }[];
        }) => {
          data.errors.forEach((item) => {
            const { field } = item;

            setError(field, {
              type: 'custom',
              message: item.message,
            });

            enqueueSnackbar(item.message, { variant: 'error' });
          });
        }
      );
    },
  });

  useEffect(() => {
    reset({
      new_program_uuid: patientData?.program_uuid || '',
      change_at: '',
    });
  }, [patientData, reset]);

  // Преобразуем программы в формат для Combobox
  const programOptions: ComboboxOption<string>[] = useMemo(() => {
    if (!patientData || !programs || !defaultProgram) {
      return [];
    }
    
    const filteredPrograms = swapElements(programs, 0, programs.indexOf(defaultProgram))?.filter(
      (type) =>
        (type?.allowedSlotPatientTypes && type?.allowedSlotPatientTypes?.includes('undefined')) ||
        type?.allowedSlotPatientTypes?.includes(patientData?.patientType?.value)
    ) || [];

    return filteredPrograms.map((program) => ({
      label: program.title,
      value: program.uuid,
    }));
  }, [patientData, programs, defaultProgram]);

  // Находим текущую выбранную программу
  const selectedProgramOption = useMemo(() => {
    const currentValue = watch('new_program_uuid');
    return currentValue ? programOptions.find((option) => option.value === currentValue) : undefined;
  }, [watch('new_program_uuid'), programOptions]);

  return (
    <Dialog open={open} onClose={modalClose} sx={{ ...dialogStyleAddModal, fullWidth: 'true' }}>
      <Box sx={modalHeaderStyle}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h3" sx={modalTitleStyle}>
            Редактирование программы пациента
          </Typography>
          <Typography variant="subtitle1" sx={modalSubtitleStyle}>
            Изменения будут применены в ближайшее время.
          </Typography>
        </Box>
        <IconButton aria-label="close modal" sx={{ width: '40px', height: '40px' }} onClick={modalClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box component="form" sx={{ ...modalFormStyle, overflow: 'visible' }} onSubmit={handleSubmit(handleEditProgram)}>
        <Box>
          <Box sx={{ ...modalFormWrapStyle(false), marginBottom: 0 }}>
            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="new_program_uuid"
                  render={({ field: { onChange } }) => (
                    <>
                      <style>
                        {`
                          [data-radix-popper-content-wrapper] {
                            z-index: 1400 !important;
                          }
                        `}
                      </style>
                      <Combobox<string>
                        label="Выберите программу"
                        placeholder="Выберите программу"
                        options={programOptions}
                        value={selectedProgramOption?.value}
                        onChange={(_newValue, option) => {
                          if (option) {
                            onChange(option.value);
                            setValue('new_program_uuid', option.value, { shouldDirty: true });
                          } else {
                            onChange('');
                            setValue('new_program_uuid', '', { shouldDirty: true });
                          }
                        }}
                        searchable
                        clearable
                        disabled={isLoadingEditProgram}
                        error={!!errors.new_program_uuid?.message}
                        helperProps={{
                          hint: errors.new_program_uuid?.message || '',
                          textColor: {
                            surface: 'light',
                            variant: 'secondary'
                          }
                        }}
                        required
                        size="m"
                      />
                    </>
                  )}
                />
              </Box>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="change_at"
                  render={({ field: { onChange } }) => {
                    return (
                      <DatePicker
                        mode="single"
                        label="Дата начала обслуживания"
                        placeholder="Дата начала обслуживания"
                        value={changeAtInput}
                        onChange={(newValue) => {
                          // Всегда сохраняем вводимую строку, чтобы разрешить ручной ввод
                          setChangeAtInput(newValue || undefined);
                          if (newValue) {
                            try {
                              // Парсим дату из формата dd.MM.yyyy
                              const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                              if (isValid(parsedDate)) {
                                // Проверяем, не заблокирована ли дата
                                if (!disabledDays(parsedDate)) {
                                  const formattedDate = format(parsedDate, 'yyyy-MM-dd');
                                  onChange(formattedDate);
                                  setValue('change_at', formattedDate, { shouldDirty: true });
                                } else {
                                  // Если дата заблокирована, показываем ошибку
                                  setError('change_at', {
                                    type: 'custom',
                                    message: 'Выбранная дата недоступна',
                                  });
                                }
                              }
                            } catch {
                              // Если не удалось распарсить, игнорируем
                            }
                          } else {
                            onChange('');
                            setValue('change_at', '', { shouldDirty: true });
                          }
                        }}
                        disabled={isLoadingEditProgram}
                        error={!!errors.change_at?.message}
                        helperProps={{
                          hint: errors.change_at?.message || '',
                          textColor: {
                            surface: 'light',
                            variant: 'secondary'
                          }
                        }}
                        required
                        size="m"
                        calendarProps={{
                          disabled: (date: Date) => disabledDays(date),
                        }}
                      />
                    );
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...modalFormWrapStyle(true), marginTop: '8px', paddingTop: 0,  justifyContent: 'flex-end',}}>
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
            }}
          >
            <SpectrumButton
              variant="secondary"
              size="s"
              onClick={handleClose}
            >
              Отмена
            </SpectrumButton>
            <TooltipActionsInfo
              placement="top"
              title={
                defaultProgram?.uuid === watch('new_program_uuid')
                  ? 'Невозможно изменить программу пациента на существующую'
                  : ''
              }
            >
              <Box component="span">
                <SpectrumButton
                  variant="promo"
                  size="s"
                  type="submit"
                  disabled={isLoadingEditProgram || !isDirty || defaultProgram?.uuid === watch('new_program_uuid')}
                >
                  Сохранить
                </SpectrumButton>
              </Box>
            </TooltipActionsInfo>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
